import React, { useState } from 'react';
import { X, Trash2, Plus, Users } from 'lucide-react';

const ManageVolunteersModal = ({ event, onClose, onSaved }) => {
    const [volunteers, setVolunteers] = useState(event.volunteers || []);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const [form, setForm] = useState({ username: '', password: '123', role: 'volunteer' });

    const handleAdd = () => {
        const username = form.username.trim();
        const password = form.password.trim() || '123';
        
        if (!username) return;
        if (volunteers.some(v => v.username === username)) {
            setError('Username already exists');
            return;
        }

        setVolunteers(prev => [...prev, { username, password, role: form.role }]);
        setForm({ username: '', password: '123', role: 'volunteer' });
        setError(null);
    };

    const handleRemove = (usernameToRemove) => {
        setVolunteers(prev => prev.filter(v => v.username !== usernameToRemove));
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/events/${event._id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ volunteers })
            });
            const data = await res.json();
            if (data.success) {
                if (onSaved) onSaved(volunteers);
                onClose();
            } else {
                setError(data.message || 'Failed to save volunteers');
            }
        } catch (err) {
            setError('Error connecting to server');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex justify-center items-center p-4 sm:p-6">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="shrink-0 px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
                            <Users size={20} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 leading-tight">Manage Volunteers</h2>
                            <p className="text-xs text-gray-500 font-medium">Volunteers can check-in participants. Admins can also undo check-ins.</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                    >
                        <X size={20} className="text-gray-400" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {error && (
                        <div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-xl border border-red-100">
                            {error}
                        </div>
                    )}

                    {/* Add Form */}
                    <div className="bg-gray-50 p-4 border rounded-xl space-y-4">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">
                            Add New Volunteer
                        </label>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <input
                                placeholder="Username"
                                value={form.username}
                                onChange={e => setForm({ ...form, username: e.target.value })}
                                className="flex-[2] px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 min-w-0"
                            />
                            <input
                                placeholder="Password"
                                type="text"
                                value={form.password}
                                onChange={e => setForm({ ...form, password: e.target.value })}
                                className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 min-w-0"
                            />
                            <select
                                value={form.role}
                                onChange={e => setForm({ ...form, role: e.target.value })}
                                className="flex-1 px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="volunteer">Volunteer</option>
                                <option value="admin">Admin</option>
                            </select>
                            <button
                                onClick={handleAdd}
                                disabled={!form.username.trim()}
                                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 shrink-0 flex items-center justify-center"
                            >
                                <Plus size={18} />
                            </button>
                        </div>
                    </div>

                    {/* List */}
                    <div className="space-y-3">
                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider">
                            Current Volunteers ({volunteers.length})
                        </label>
                        
                        {volunteers.length === 0 ? (
                            <div className="text-center py-6 text-gray-400 text-sm border-2 border-dashed rounded-xl">
                                No volunteers added yet
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {volunteers.map((vol, idx) => (
                                    <div key={idx} className="flex justify-between items-center bg-white border px-4 py-3 rounded-xl shadow-sm">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <p className="font-bold text-gray-900 text-sm">{vol.username}</p>
                                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                                                    vol.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                                                }`}>
                                                    {vol.role || 'volunteer'}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-500 font-mono mt-0.5">pwd: {vol.password}</p>
                                        </div>
                                        <button
                                            onClick={() => handleRemove(vol.username)}
                                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                            title="Remove Volunteer"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="shrink-0 p-4 border-t bg-gray-50 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-bold rounded-xl hover:bg-gray-50 transition-colors"
                        disabled={saving}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        className="px-6 py-2 bg-indigo-600 text-white text-sm font-bold rounded-xl hover:bg-indigo-700 shadow-sm transition-colors flex items-center justify-center disabled:opacity-70 gap-2"
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ManageVolunteersModal;
